import type { IPeerConnection, PeerConnectionEvent } from '../types';
import { PlayerVideoBridge } from './PlayerVideoBridge';
import { NATIVE_VIDEO_CHANNEL, parseNativeVideoMessage } from './NativeVideoProtocol';
import type { VideoPresentationTarget } from './VideoPresentationTypes';
import type { WcClientStats, WcWorkerPort } from '../webcodecs/client/WebCodecsClientTypes';
import type { VideoStreamingStatus } from './NativeVideoHost';
import type { WebCodecsPaintSample } from './WebCodecsPaintSample';

export type GuestNativeVideoOptions = {
  /** App-owned worker factory; absent means WebCodecs is unavailable and only native RTP is offered. */
  createWebCodecsWorker?: () => WcWorkerPort;
  /** Each WebCodecs frame drawn, so the session can report it through the latency probe. */
  onPresented?: (sample: WebCodecsPaintSample) => void;
};

/**
 * Speaks the host's native-video protocol over an authenticated guest connection.
 * One binding per connection epoch: the host's `configure` only follows a `hello`, and a
 * guest that never says hello stays on Electron video whatever the host prefers.
 */
export class GuestNativeVideoBinding {
  private readonly bridge: PlayerVideoBridge;

  constructor(
    connection: IPeerConnection,
    onStream: (stream: MediaStream) => void,
    options: GuestNativeVideoOptions = {},
  ) {
    const peer = {
      get: () => {
        const pc = connection.getRtcPeerConnection();
        if (!pc) throw new Error('Peer connection unavailable');
        return pc;
      },
      addDataChannel: (label: string, init: RTCDataChannelInit) => connection.addDataChannel(label, init),
      send: (data: string, label: string) => connection.send(data, label),
      // peer-lite always supplies an event object; mocks may not. Normalize so a bare
      // `channelOpen` cannot throw inside the bridge's label check.
      on: (event: string, listener: (event: any) => void) =>
        connection.on(event as PeerConnectionEvent, (...args: unknown[]) => listener(args[0] ?? {})),
    };
    this.bridge = new PlayerVideoBridge(
      peer,
      onStream,
      options.createWebCodecsWorker
        ? { createWebCodecsWorker: options.createWebCodecsWorker, onPresented: options.onPresented }
        : undefined,
    );
  }

  /** Register the signaling channel before the connection starts so it rides the first offer. */
  start(): void { this.bridge.start(); }

  setPresentationTarget(target: VideoPresentationTarget | null): void {
    // Any swap or detach releases the previous wrapper's canvas, which clears the flag.
    this.bridge.setPresentationTarget(target ? this.observe(target) : null);
  }

  getVideoPeerConnection(): RTCPeerConnection | null { return this.bridge.getVideoPeerConnection(); }

  /** WebCodecs worker stats while the canvas path is active. */
  getWebCodecsStats(): WcClientStats | null { return this.bridge.getWebCodecsStats(); }

  /** Guest path snapshot for `formatVideoPathDebugLine`. */
  getVideoPathStatus(): VideoStreamingStatus | undefined {
    if (this.closed) return undefined;
    return this.bridge.getVideoPathStatus();
  }

  /**
   * True while the bridge has selected the WebCodecs canvas. Video then rides a data channel
   * rather than RTP, so neither the primary nor the native PC's inbound-rtp describes it.
   */
  isPresentingWebCodecs(): boolean { return this.presentingWebCodecs; }

  private closed = false;

  close(): void {
    this.closed = true;
    this.presentingWebCodecs = false;
    this.bridge.close();
  }

  private presentingWebCodecs = false;
  /** One wrapper per app target, so re-binding the same surface stays a no-op in the bridge. */
  private readonly observed = new WeakMap<VideoPresentationTarget, VideoPresentationTarget>();

  /** The bridge's presentation calls are the only signal of which surface currently owns video. */
  private observe(target: VideoPresentationTarget): VideoPresentationTarget {
    let wrapper = this.observed.get(target);
    if (!wrapper) {
      wrapper = {
        acquireCanvas: () => target.acquireCanvas(),
        present: (presentation) => { this.presentingWebCodecs = presentation.kind === 'webcodecs'; target.present(presentation); },
        releaseCanvas: () => { this.presentingWebCodecs = false; target.releaseCanvas(); },
      };
      this.observed.set(target, wrapper);
    }
    return wrapper;
  }

  /** True for traffic on the native-video channel; the app's data handler never sees it. */
  static isNativeVideoEvent(event: unknown): boolean {
    const record = event as { channel?: { label?: string }; data?: unknown } | undefined;
    if (record?.channel?.label === NATIVE_VIDEO_CHANNEL) return true;
    return record?.channel?.label === 'data' && parseNativeVideoMessage(record.data) !== null;
  }
}
